import { ORDER_METHODS } from '../exports/Constants';

/**
 * The backend renamed the on-site order method `room_service` to `on_site` (PROD-724), and its
 * fields `has_room_service` / `room_service_location` / `room_service_details` /
 * `room_service_tax_rate` to `has_on_site` / `on_site_table` / `on_site_table_name` /
 * `on_site_tax_rate`. The dashboard reads and sends the new names. The old value can still reach
 * it for one release (Redux Persist keeps the company from before a deploy, and smart-pricing or
 * price rows written by the previous release), so these helpers read it as `on_site`. Every helper
 * takes either shape and is idempotent. PROD-829 removes the aliases.
 *
 * Same names as shop-web's `src/utils/on-site.ts`.
 */
export const ROOM_SERVICE_ORDER_METHOD = 'room_service';

const ON_SITE_METHOD_VALUES = [ORDER_METHODS.on_site, ROOM_SERVICE_ORDER_METHOD];

export function isOnSiteMethod(method) {
  return ON_SITE_METHOD_VALUES.includes(method);
}

/** The dashboard's own value for a method the backend sent under either name. */
export function normaliseOrderMethod(method) {
  return isOnSiteMethod(method) ? ORDER_METHODS.on_site : method;
}

/** A list of order methods with the on-site method under its new name, once. */
export function normaliseOrderMethodList(list) {
  if (!Array.isArray(list)) {
    return list;
  }

  return [...new Set(list.map(normaliseOrderMethod))];
}

/** `hidden_payment_methods`: payment method → order methods it is hidden on, or `'*'` for all. */
export function normaliseOrderMethodMap(map) {
  if (!map || Array.isArray(map) || typeof map !== 'object') {
    return map;
  }

  return Object.fromEntries(
    Object.entries(map).map(([key, value]) => [key, Array.isArray(value) ? normaliseOrderMethodList(value) : value]),
  );
}

/** `list.includes(method)`, matching the on-site method under either name. */
export function includesOrderMethod(list, method) {
  if (!Array.isArray(list) || method == null) {
    return false;
  }

  return isOnSiteMethod(method) ? list.some(isOnSiteMethod) : list.includes(method);
}

/** `record[method]` for a record keyed by order method, e.g. `order_method_prices`. */
export function valueForOrderMethod(record, method) {
  if (!record || method == null) {
    return undefined;
  }

  if (!isOnSiteMethod(method)) {
    return record[method];
  }

  return record[method] ?? ON_SITE_METHOD_VALUES.map((value) => record[value]).find((value) => value != null);
}

/** The keys of a record keyed by order method, the on-site key under its new name, once. */
export function orderMethodKeys(record) {
  return record ? normaliseOrderMethodList(Object.keys(record)) : [];
}

/** A field sent under its new name, or under the old one by a backend from before the rename. */
export function onSiteFieldOf(object, newKey, oldKey) {
  return object?.[newKey] ?? object?.[oldKey];
}

/**
 * The company as the dashboard reads it: the switch under `has_on_site` (and no
 * `has_room_service`, so a stale copy can't ride along in a payload built from the company), the
 * hidden lists under the new method name.
 */
export function normaliseCompany(company) {
  if (!company) {
    return company;
  }

  const { has_room_service: hasRoomService, ...rest } = company;
  const hasOnSite = company.has_on_site ?? hasRoomService;

  return {
    ...rest,
    ...(hasOnSite !== undefined && { has_on_site: hasOnSite }),
    ...('hidden_order_methods' in company && {
      hidden_order_methods: normaliseOrderMethodList(company.hidden_order_methods),
    }),
    ...('hidden_payment_methods' in company && {
      hidden_payment_methods: normaliseOrderMethodMap(company.hidden_payment_methods),
    }),
  };
}

/** An order as the dashboard reads it: the method under its new name. */
export function normaliseOrder(order) {
  if (!order || !isOnSiteMethod(order.method) || order.method === ORDER_METHODS.on_site) {
    return order;
  }

  return { ...order, method: ORDER_METHODS.on_site };
}
